import React, { useRef, useState } from "react";
import { Upload, Loader2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function defaultAvatar(seed) {
  const s = encodeURIComponent((seed || "PodiumOne").trim() || "PodiumOne");
  return `https://api.dicebear.com/7.x/initials/svg?seed=${s}&backgroundType=gradientLinear&fontWeight=700`;
}

export function ImageUpload({ value, onChange, seedName, label = "Logo / avatar", testId = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const preview = value || defaultAvatar(seedName);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      {label && <div className="text-white/70 text-sm mb-1.5">{label}</div>}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-2xl overflow-hidden glass-strong flex items-center justify-center shrink-0">
          <img src={preview} alt="avatar preview" className="w-full h-full object-cover" />
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleFile} className="hidden" data-testid={`${testId}-input`} />
          <button
            type="button"
            data-testid={`${testId}-btn`}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm font-medium hover:border-white/25 transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {value ? "Change image" : "Upload image"}
          </button>
          {value ? (
            <button type="button" data-testid={`${testId}-remove`} onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-red-300 transition-colors">
              <X className="w-3 h-3" /> Remove — use default
            </button>
          ) : (
            <span className="text-xs text-white/40">Optional — a default avatar is used if none.</span>
          )}
        </div>
      </div>
    </div>
  );
}
